import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "src/contexts/AuthContext";
import { AlenRoom } from "src/interfaces/IZone";
import { AlenProperty } from "src/interfaces/IProperty";
import { AlenAccessToken } from "../authService";
import { makeAlenAxios } from ".";

type FetchAlenZonesResponse = {
  result: AlenProperty & { rooms: AlenRoom[] };
};
export const fetchAlenRooms = async (
  alenAccess: AlenAccessToken,
  alenPropertyId: number
) => {
  const alenAxios = makeAlenAxios(alenAccess);
  const response = await alenAxios.get<FetchAlenZonesResponse>(
    `/homes/${alenPropertyId}/rooms`
  );
  return response.data.result.rooms;
};

const useAlenRooms = (alenPropertyId: number | undefined) => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState<AlenRoom[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AxiosError | undefined>();
  useEffect(() => {
    if (user?.alen) {
      setLoading(true);
      setData([]);
      if (alenPropertyId) {
        fetchAlenRooms(user.alen, alenPropertyId)
          .then((rooms) => {
            setData(rooms);
          })
          .catch((err: unknown) => {
            if (err instanceof AxiosError) {
              setError(err);
            } else {
              // eslint-disable-next-line no-console
              console.error(err);
            }
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    } else {
      setData([]);
      setLoading(false);
    }
  }, [user, alenPropertyId]);

  const refetchRooms = useCallback(async () => {
    setLoading(true);
    setData([]);
    if (user?.alen && alenPropertyId) {
      fetchAlenRooms(user.alen, alenPropertyId)
        .then((rooms) => {
          setData(rooms);
        })
        .catch((err: unknown) => {
          if (err instanceof AxiosError) {
            setError(err);
          } else {
            // eslint-disable-next-line no-console
            console.error(err);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [alenPropertyId, user?.alen]);

  const alenRoomData = useMemo(() => data, [data]);

  return {
    data: alenRoomData,
    loading,
    error,
    refetch: refetchRooms,
  };
};

export default useAlenRooms;
